import type { ICohortQueryModel } from "@performyard/models";

import type { CycleType } from "./Config";
import type { ReviewCycleWizardInitialData } from "./ReviewCycleWizard";

import type { FormsByType } from "./steps/AddFormsStep/types";

import { STEP_ID as ADD_FORMS_STEP_ID } from "./steps/AddFormsStep/constants";
import { AUTOMATION_RULES_STEP_ID } from "./steps/AutomatedSubjectsStep/constants";
import type { SettingsData } from "./steps/CycleSettingsStep/schema";

const BASIC_INFO_STEP_ID = "basic-info";

/** The source cycle's settings, derived from the details endpoint by useCopyInitialData. */
export type ReviewCycleCopySeed = {
	type: CycleType;
	name: string;
	startDate: Date;
	adminsOnly: boolean;
	group: { id: string; name: string } | null;
	template: { id: string; name: string } | null;
	automation: { type: "recurring" | "oneTime"; endDate: Date | null; userQuery: ICohortQueryModel } | null;
};

/** Maps the source cycle's settings + forms into wizard `initialData` keyed by step id. */
export const buildCopyInitialData = (copy: ReviewCycleCopySeed, forms: FormsByType): ReviewCycleWizardInitialData => {
	const { type, name, startDate, adminsOnly, group, template, automation } = copy;
	const basicInfo: Record<string, unknown> = {
		cycleType: type,
		cycleName: `${name} (copy)`,
		lockReviews: adminsOnly,
		startDate,
	};

	if (type === "automated" && automation) {
		basicInfo.automationType = automation.type;
		basicInfo.automationEndDate = automation.endDate;
	}

	if (type === "group") {
		basicInfo.group = group;
	}

	const initialData: ReviewCycleWizardInitialData = {
		[BASIC_INFO_STEP_ID]: basicInfo as SettingsData,
		[ADD_FORMS_STEP_ID]: {
			selectedTemplate: template,
			forms,
		},
	};

	if (type === "automated" && automation) {
		initialData[AUTOMATION_RULES_STEP_ID] = { autoflowUserQuery: automation.userQuery };
	}

	return initialData;
};
